import { useSuspenseQuery } from '@tanstack/react-query';

import { fetchAssessmentProgress } from '../api/assessmentApi';
import { assessmentKeys } from './useAssessment';
import { useAssessmentStore } from '../stores/useAssessmentStore';
import type { CandidateAnswer } from '../types';

export function useAssessmentProgress(publicUrl: string) {
  const applyProgress = useAssessmentStore((s) => s.applyProgress);
  const setStarted = useAssessmentStore((s) => s.setStarted);

  return useSuspenseQuery({
    queryKey: assessmentKeys.progress(publicUrl),
    queryFn: async () => {
      const data = await fetchAssessmentProgress(publicUrl);

      if (data.stepEvaluation.status === 'INIT' || !data.progress) {
        return data;
      }

      const { stepEvaluation, progress } = data;

      // Build a minimal answers map from server-side answered list
      const answers: Record<string, CandidateAnswer> = {};
      for (const a of progress.answers) {
        answers[a.question] = {
          questionId: a.question,
          type: 'text', // type not returned by progress — only used for count tracking
          value: null,
          skipped: false,
          iDontKnow: false,
          timeSpent: 0,
          submittedAt: new Date(a.answeredAt).getTime(),
        };
      }

      // Both dates must be present on an INPROGRESS step. If either is missing
      // the backend data is corrupt — throw so the ErrorBoundary catches it
      // rather than silently using Date.now() and immediately firing the timer.
      if (!stepEvaluation.startDate || !stepEvaluation.plannedEndDate) {
        throw new Error('Assessment progress is missing start/end dates');
      }

      const startedAt = new Date(stepEvaluation.startDate).getTime();
      const endsAt = new Date(stepEvaluation.plannedEndDate).getTime();

      applyProgress({
        currentSectionIndex: 0, // backend has no sections — always section 0
        currentQuestionIndex: stepEvaluation.currentQuestionIndex,
        answers,
        startedAt,
        endsAt,
        cheatingTries: stepEvaluation.cheatingTries,
      });

      // Also update the timer via setStarted
      setStarted(startedAt, endsAt);

      return data;
    },
    staleTime: 0,
    retry: false,
  });
}
